import { ByteReader } from "./bytes";
import { FORMAT_VERSION, MAGIC, type Blob, type Header } from "./format";

export async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Bytes that may still be arriving: a whole file, or a download in progress.
 * Readers wait for the range they need, so playback can start as soon as the
 * beginning has arrived.
 */
export class ByteSource {
  private buf: Uint8Array;
  received = 0;
  /** Total size, when known. */
  total: number;
  done = false;
  error: Error | null = null;
  private waiters: { end: number; resolve: () => void; reject: (e: Error) => void }[] = [];
  private listeners: (() => void)[] = [];

  constructor(total: number) {
    this.total = total;
    this.buf = new Uint8Array(Math.max(total, 1 << 20));
  }

  static whole(bytes: Uint8Array): ByteSource {
    const s = new ByteSource(bytes.length);
    s.append(bytes);
    s.finish();
    return s;
  }

  /** Streams `url`, appending as data arrives. */
  static fetch(url: string): Promise<ByteSource> {
    // Revalidate: replays may be re-recorded under the same name.
    return fetch(url, { cache: "no-cache" }).then((res) => {
      if (!res.ok || !res.body) throw new Error(`Couldn't load ${url}: ${res.status} ${res.statusText}`);
      const s = new ByteSource(Number(res.headers.get("Content-Length")) || 0);
      const reader = res.body.getReader();
      const pump = async () => {
        try {
          for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            s.append(value);
          }
          s.finish();
        } catch (e) {
          s.fail(e instanceof Error ? e : new Error(String(e)));
        }
      };
      void pump();
      return s;
    });
  }

  onProgress(f: () => void): void {
    this.listeners.push(f);
  }

  append(bytes: Uint8Array): void {
    if (this.received + bytes.length > this.buf.length) {
      const bigger = new Uint8Array(Math.max(this.buf.length * 2, this.received + bytes.length));
      bigger.set(this.buf.subarray(0, this.received));
      this.buf = bigger;
    }
    this.buf.set(bytes, this.received);
    this.received += bytes.length;
    this.settle();
  }

  finish(): void {
    this.done = true;
    this.total = this.received;
    this.settle();
  }

  fail(e: Error): void {
    this.error = e;
    this.settle();
  }

  private settle(): void {
    this.waiters = this.waiters.filter((w) => {
      if (w.end <= this.received) w.resolve();
      else if (this.error) w.reject(this.error);
      else if (this.done) w.reject(new Error("The replay file ended early."));
      else return true;
      return false;
    });
    for (const f of this.listeners) f();
  }

  /** Resolves once bytes `[0, end)` have arrived. */
  wait(end: number): Promise<void> {
    if (end <= this.received) return Promise.resolve();
    if (this.error) return Promise.reject(this.error);
    if (this.done) return Promise.reject(new Error("The replay file ended early."));
    return new Promise((resolve, reject) => this.waiters.push({ end, resolve, reject }));
  }

  has(end: number): boolean {
    return end <= this.received;
  }

  async slice(start: number, end: number): Promise<Uint8Array> {
    await this.wait(end);
    return this.buf.subarray(start, end);
  }

  /** Download progress 0..1 (-1 when the size is unknown). */
  get progress(): number {
    return this.done ? 1 : this.total ? this.received / this.total : -1;
  }
}

/** Static layers, one byte per hex, row-major (`r * width + q`). */
export interface Statics {
  terrain: Uint8Array;
  track: Uint8Array;
  lanes: Uint8Array;
  corridor: Uint8Array;
  producers: { tile: number; faction: number }[];
  /** Ground height for shading (0..255); null in replays that predate it. */
  relief: Uint8Array | null;
  /** Each hex's region (`REGION`): inside the map, the storm wall, the
   *  ground outside it, or void; null in worlds without one (the map's
   *  rectangle is all there is). */
  region: Uint8Array | null;
}

/** A hex's region (`Statics.region`). */
export const REGION = { INSIDE: 0, WALL: 1, OUTSIDE: 2, VOID: 3 } as const;

/** The static layers from a decompressed static blob (replays and the live
 *  stream alike). */
export function parseStatics(header: Header, raw: Uint8Array): Statics {
  const tiles = header.world.width * header.world.height;
  const layer = (k: number) => raw.subarray(k * tiles, (k + 1) * tiles);
  const pr = new ByteReader(raw.subarray(4 * tiles));
  const producers = [];
  for (let i = 0, n = pr.u32(); i < n; i++) producers.push({ tile: pr.u32(), faction: pr.u8() });
  const rest = raw.subarray(4 * tiles + pr.at);
  const relief = rest.length >= tiles ? rest.subarray(0, tiles) : null;
  const region = rest.length >= 2 * tiles ? rest.subarray(tiles, 2 * tiles) : null;
  return { terrain: layer(0), track: layer(1), lanes: layer(2), corridor: layer(3), producers, relief, region };
}

/** A replay: header and statics parsed up front, chunks decompressed on
 *  demand (and waited for, while the file is still downloading). */
export class ReplayFile {
  private cache = new Map<number, Promise<Uint8Array>>();
  private order: number[] = [];

  private constructor(
    readonly header: Header,
    readonly statics: Statics,
    readonly source: ByteSource,
    private blobsAt: number,
  ) {}

  /** Parses as soon as the header and static layers have arrived. */
  static async open(source: ByteSource): Promise<ReplayFile> {
    const head = await source.slice(0, 16);
    if (new TextDecoder().decode(head.subarray(0, 8)) !== MAGIC) throw new Error("Not a Ferro replay file.");
    const r = new ByteReader(head);
    r.at = 8;
    const version = r.u32();
    if (version !== FORMAT_VERSION) throw new Error(`Replay format ${version}; this viewer reads format ${FORMAT_VERSION}.`);
    const len = r.u32();
    const header = JSON.parse(new TextDecoder().decode(await source.slice(16, 16 + len))) as Header;
    const blobsAt = 16 + len;
    const sb = header.static_blob;
    const raw = await gunzip((await source.slice(blobsAt + sb.offset, blobsAt + sb.offset + sb.len)).slice());
    return new ReplayFile(header, parseStatics(header, raw), source, blobsAt);
  }

  /** Index of the chunk holding `frame`. */
  chunkOf(frame: number): number {
    const chunks = this.header.chunks;
    let lo = 0;
    let hi = chunks.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (chunks[mid].first_frame <= frame) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  private end(b: Blob): number {
    return this.blobsAt + b.offset + b.len;
  }

  /** Whether chunk `i` has fully downloaded. */
  arrived(i: number): boolean {
    return this.source.has(this.end(this.header.chunks[i].blob));
  }

  /** Frames available so far (all of them once downloaded). */
  framesArrived(): number {
    const chunks = this.header.chunks;
    let n = 0;
    for (const c of chunks) {
      if (!this.source.has(this.end(c.blob))) break;
      n = c.first_frame + c.frames;
    }
    return n;
  }

  /** Decompressed bytes of chunk `i` (cached; the 4 most recent are kept). */
  chunk(i: number): Promise<Uint8Array> {
    let p = this.cache.get(i);
    if (!p) {
      const b = this.header.chunks[i].blob;
      p = this.source.slice(this.blobsAt + b.offset, this.end(b)).then((bytes) => gunzip(bytes.slice()));
      this.cache.set(i, p);
      p.catch(() => this.cache.delete(i));
    }
    this.order = [i, ...this.order.filter((c) => c !== i)];
    for (const old of this.order.splice(4)) this.cache.delete(old);
    return p;
  }
}
